export type OverlapBooking = {
  id: string
  start: string
  end: string
  event_type?: string | null
}

export type CalendarDisplayItem<T extends OverlapBooking> =
  | { kind: "single"; booking: T }
  | { kind: "group"; bookings: T[]; start: string; end: string }

function isGroupable(booking: OverlapBooking): boolean {
  return booking.event_type !== "pausa" && booking.event_type !== "time_block"
}

function startMs(booking: OverlapBooking): number {
  return new Date(booking.start).getTime()
}

function endMs(booking: OverlapBooking): number {
  return new Date(booking.end).getTime()
}

export function overlapGroupTitle(count: number): string {
  return count === 1 ? "1 cita" : `${count} citas`
}

export function collapseOverlappingBookings<T extends OverlapBooking>(
  bookings: T[],
  minGroupSize = 2,
): CalendarDisplayItem<T>[] {
  const locked: CalendarDisplayItem<T>[] = []
  const groupable: T[] = []

  for (const booking of bookings) {
    if (isGroupable(booking)) groupable.push(booking)
    else locked.push({ kind: "single", booking })
  }

  const sorted = [...groupable].sort((a, b) => startMs(a) - startMs(b) || endMs(a) - endMs(b))
  const clusters: T[][] = []
  let current: T[] = []
  let clusterEnd = Number.NEGATIVE_INFINITY

  for (const booking of sorted) {
    const start = startMs(booking)
    if (current.length === 0 || start < clusterEnd) {
      current.push(booking)
      clusterEnd = Math.max(clusterEnd, endMs(booking))
      continue
    }
    clusters.push(current)
    current = [booking]
    clusterEnd = endMs(booking)
  }
  if (current.length > 0) clusters.push(current)

  const collapsed: CalendarDisplayItem<T>[] = []
  for (const cluster of clusters) {
    if (cluster.length < minGroupSize) {
      for (const booking of cluster) {
        collapsed.push({ kind: "single", booking })
      }
      continue
    }
    const start = Math.min(...cluster.map(startMs))
    const end = Math.max(...cluster.map(endMs))
    collapsed.push({
      kind: "group",
      bookings: cluster,
      start: new Date(start).toISOString(),
      end: new Date(end).toISOString(),
    })
  }

  return [...locked, ...collapsed]
}
